import { useCallback } from 'react';
import type { AssignmentAdmin } from '../../components/common/events/EventAssignmentSettingsModal';

type EventWithAssignments = {
  event_uuid?: string; assigned_admins?: AssignmentAdmin[]; assignedAdmins?: AssignmentAdmin[];
  is_assigned_to_me?: boolean; isAssignedToMe?: boolean;
};
type Setter<T> = React.Dispatch<React.SetStateAction<T[]>>;
type Props<T extends EventWithAssignments> = {
  currentAdminUserId: string; filterAssignedOnly: boolean;
  setAllFetchedEvents: Setter<T>; setEvents: Setter<T>; setCalendarEvents: Setter<T>;
  calendarCacheRef: React.MutableRefObject<Map<string, T[]>>;
};
export default function useEventListAssignmentUpdates<T extends EventWithAssignments>({
  currentAdminUserId, filterAssignedOnly, setAllFetchedEvents, setEvents, setCalendarEvents, calendarCacheRef,
}: Props<T>) {
  return useCallback((eventUuid: string, assignments: AssignmentAdmin[]) => {
    const assignedToMe = assignments.some(admin => String(admin.admin_user_id ?? admin.id) === currentAdminUserId);
    const update = (events: T[], assignedOnly = false) => events
      .map(event => event.event_uuid === eventUuid ? { ...event, assigned_admins: assignments, assignedAdmins: assignments,
        is_assigned_to_me: assignedToMe, isAssignedToMe: assignedToMe } : event)
      .filter(event => !(assignedOnly && event.event_uuid === eventUuid && !assignedToMe));
    setAllFetchedEvents(previous => update(previous));
    setEvents(previous => update(previous, filterAssignedOnly));
    setCalendarEvents(previous => update(previous, filterAssignedOnly));
    // Assigned-month caches may need an event that was absent before this edit.
    calendarCacheRef.current.clear();
  }, [currentAdminUserId, filterAssignedOnly, setAllFetchedEvents, setEvents, setCalendarEvents, calendarCacheRef]);
}
